import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import TicketService from "../../services/TicketService";
import AppointmentService from "../../services/AppointmentService";
import {
  FlowAnswerVariableSelect,
  FlowVariableInsertPicker,
} from "../../components/flow-variables";

const AnswerVariableField = ({
  label,
  value,
  onChange,
  customVariables,
  placeholder,
  hint,
}) => (
  <label style={{ fontSize: 12 }}>
    {label}
    <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
      <input
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={{
          flex: "1 1 180px",
          minWidth: 0,
          padding: 8,
          border: "1px solid #cbd5e1",
          borderRadius: 6,
        }}
      />
      <FlowAnswerVariableSelect
        value={value || ""}
        onChange={onChange}
        customVariables={customVariables}
      />
    </div>
    {hint ? (
      <div style={{ marginTop: 4, color: "#64748b", fontSize: 11 }}>{hint}</div>
    ) : null}
  </label>
);

const ACTIONS = [
  { value: "ticket.package.departures", label: "Ticket: List travel dates" },
  { value: "ticket.departure.itinerary", label: "Ticket: List itinerary" },
  { value: "ticket.trip.addons", label: "Ticket: Show hotels & vehicles" },
  { value: "ticket.tier.availability", label: "Ticket: Check availability" },
  { value: "ticket.hold.create", label: "Ticket: Reserve trip / seats" },
  { value: "ticket.payment.attach", label: "Ticket: Prepare payment" },
  { value: "appointment.availability", label: "Appointment: Check slots" },
  { value: "appointment.hold.create", label: "Appointment: Reserve slot" },
  { value: "appointment.hold.confirm", label: "Appointment: Confirm booking (no payment)" },
  { value: "appointment.payment.attach", label: "Appointment: Prepare payment" },
  { value: "cart.sync_state", label: "Cart: Refresh cart (use before router)" },
  { value: "cart.validate_stock", label: "Cart: Check out-of-stock (sets {{invaliditems}})" },
  { value: "cart.remove_oos", label: "Cart: Remove out-of-stock items" },
  { value: "cart.delete_orphans", label: "Cart: Remove old items" },
  { value: "cart.relink_orphans", label: "Cart: Keep old items in cart" },
  { value: "cart.clear", label: "Cart: Clear all items" },
];

const DEFAULT_DATA = {
  actionId: "ticket.hold.create",
  title: "Reserve ticket",
  ticketEventId: "",
  ticketPackageEventId: "",
  ticketTierId: "",
  qty: "",
  seatVariable: "",
  passengerVariable: "",
  travelBooking: true,
  hotelVariable: "",
  vehicleVariable: "",
  memberCountVariable: "",
  appointmentTypeId: "",
  appointmentResourceId: "",
  appointmentStartVariable: "",
};

const parseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const VerticalActionNode = ({ data, id, selected = false }) => {
  const safeData = { ...DEFAULT_DATA, ...(data || {}) };
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [draft, setDraft] = useState(safeData);
  const [ticketEvents, setTicketEvents] = useState([]);
  const [ticketTiersByEvent, setTicketTiersByEvent] = useState({});
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [appointmentResources, setAppointmentResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setShowMenu(false);
    };
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const loadOptions = async () => {
    setLoading(true);
    try {
      const [events, types, resources] = await Promise.all([
        TicketService.listEvents().catch(() => []),
        AppointmentService.listTypes().catch(() => []),
        AppointmentService.listResources().catch(() => []),
      ]);
      setTicketEvents(events || []);
      setAppointmentTypes(types || []);
      setAppointmentResources(resources || []);

      const tierPairs = await Promise.all((events || []).map(async (event) => [
        String(event.id),
        await TicketService.listTiers(event.id).catch(() => []),
      ]));
      setTicketTiersByEvent(Object.fromEntries(tierPairs));
    } finally {
      setLoading(false);
    }
  };

  const openEdit = async () => {
    setDraft({ ...DEFAULT_DATA, ...(data || {}) });
    setShowMenu(false);
    setShowEditPopup(true);
    await loadOptions();
  };

  const usesCustomerTravelSession = (actionId, travelBooking = true) => actionId === "ticket.departure.itinerary"
    || actionId === "ticket.trip.addons"
    || actionId === "ticket.payment.attach"
    || (actionId === "ticket.hold.create" && travelBooking !== false);

  const usesSessionOnlyMessage = (actionId, travelBooking = true) => actionId === "ticket.departure.itinerary"
    || actionId === "ticket.trip.addons"
    || actionId === "ticket.payment.attach";

  const save = () => {
    const next = {
      ...draft,
      title: draft.title || ACTIONS.find((action) => action.value === draft.actionId)?.label || "Vertical action",
    };
    if (usesCustomerTravelSession(next.actionId, next.travelBooking)) {
      next.ticketEventId = "";
      next.ticketTierId = "";
    }
    data.onChangeVerticalAction?.(id, next);
    setShowEditPopup(false);
  };

  const eventKind = (event) => {
    const meta = parseJson(event?.metadata_json, {});
    if (meta.kind === "package") return "package";
    if (event?.parent_event_id) return "departure";
    return meta.kind || "event";
  };
  const packageEvents = ticketEvents.filter((event) => eventKind(event) === "package");
  const bookableEvents = ticketEvents.filter((event) => eventKind(event) !== "package");
  const isPackageDeparturesAction = draft.actionId === "ticket.package.departures";
  const isDepartureItineraryAction = draft.actionId === "ticket.departure.itinerary";
  const isTripAddonsAction = draft.actionId === "ticket.trip.addons";
  const isPaymentAttachAction = draft.actionId === "ticket.payment.attach";
  const isTravelReserveAction = draft.actionId === "ticket.hold.create" && draft.travelBooking !== false;
  const isTierAvailabilityAction = draft.actionId === "ticket.tier.availability";
  const usesSessionOnlyUi = usesSessionOnlyMessage(draft.actionId, draft.travelBooking);
  const selectedTier = (ticketTiersByEvent[String(draft.ticketEventId)] || [])
    .find((tier) => String(tier.id) === String(draft.ticketTierId));
  const seatMap = parseJson(selectedTier?.metadata_json, {}).seat_map;
  const seatLabels = (seatMap?.sections || []).flatMap((section) => section.seats || []);

  const isTicketAction = String(draft.actionId || "").startsWith("ticket.");
  const isAppointmentAction = String(draft.actionId || "").startsWith("appointment.");
  const isCartAction = String(draft.actionId || "").startsWith("cart.");
  const isSyncCartStateAction = draft.actionId === "cart.sync_state";
  const availableVariables = Array.isArray(data?.availableVariables) ? data.availableVariables : [];

  const placeholderSectionIds = useMemo(() => {
    if (isTicketAction) return ["default", "ticket_booking", "custom"];
    if (isAppointmentAction) return ["default", "appointment", "custom"];
    if (isCartAction) return ["default", "custom"];
    return ["default", "ticket_booking", "appointment", "custom"];
  }, [isTicketAction, isAppointmentAction, isCartAction]);

  const copyPlaceholder = (variableId) => {
    const token = `{{${variableId}}}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(token).catch(() => {});
    }
  };

  const popup = (
    <div
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
      onClick={() => setShowEditPopup(false)}
    >
      <div
        style={{ width: 720, maxWidth: "94vw", maxHeight: "88vh", overflow: "auto", background: "#fff", borderRadius: 10, padding: 16 }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>Configure Vertical Action</h3>
        <p style={{ color: "#64748b", fontSize: 13 }}>
          Optional node for booking, ticketing, payments, and future vertical workflows. Normal message,
          question, media, template, webhook, and payment nodes remain fully generic.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <label style={{ fontSize: 12 }}>
            Node label
            <input
              value={draft.title || ""}
              onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
              style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
            />
          </label>
          <label style={{ fontSize: 12 }}>
            Action
            <select
              value={draft.actionId}
              onChange={(event) => {
                const actionId = event.target.value;
                setDraft((prev) => ({
                  ...prev,
                  actionId,
                  ...(usesCustomerTravelSession(actionId, prev.travelBooking)
                    ? { ticketEventId: "", ticketTierId: "" }
                    : {}),
                }));
              }}
              style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
            >
              {ACTIONS.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
            </select>
          </label>
        </div>

        {loading ? <div style={{ fontSize: 12, color: "#64748b" }}>Loading configured inventory...</div> : null}

        {isTicketAction && (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <b>Ticket configuration</b>
            {isPackageDeparturesAction ? (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 8,
                  padding: 10,
                  borderRadius: 6,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1e3a8a",
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                After this node, use Message with {"{{tour_package_details}}"} (package info) and{" "}
                {"{{travel_dates_text}}"} (each date, duration, slots left). Ask the customer to pick a date (1, 2, 3…). Save the answer as{" "}
                <code>selected_travel_date</code> (or <code>selected_departure</code>).
              </div>
            ) : null}
            {isDepartureItineraryAction ? (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 8,
                  padding: 10,
                  borderRadius: 6,
                  background: "#faf5ff",
                  border: "1px solid #e9d5ff",
                  color: "#581c87",
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                Place after the customer picks a travel date. Next message: use{" "}
                {"{{travel_itinerary_text}}"} (itinerary for the selected departure only).
              </div>
            ) : null}
            {isTripAddonsAction ? (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 8,
                  padding: 10,
                  borderRadius: 6,
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  color: "#14532d",
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                Place after the customer picks a travel date. Use {"{{hotel_options_text}}"} and{" "}
                {"{{vehicle_options_text}}"} in messages. Save answers as <code>selected_hotel</code> /{" "}
                <code>selected_vehicle</code> (or type <code>skip</code>). After <code>member_count</code> is set,
                show {"{{travel_price_summary}}"} before reserve.
              </div>
            ) : null}
            {isTierAvailabilityAction ? (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 8,
                  padding: 10,
                  borderRadius: 6,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1e3a8a",
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                After this node: {"{{ticket_availability_text}}"}, {"{{ticket_available_seats_text}}"}, {"{{ticket_seats_text}}"}.
              </div>
            ) : null}
            {isPaymentAttachAction ? (
              <div
                style={{
                  marginTop: 8,
                  marginBottom: 8,
                  padding: 10,
                  borderRadius: 6,
                  background: "#fef3c7",
                  border: "1px solid #fde68a",
                  color: "#92400e",
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                Uses the hold from <strong>Reserve trip</strong> (<code>last_ticket_hold_id</code>). Place after Reserve trip in the flow.
              </div>
            ) : null}
            {isPackageDeparturesAction ? (
              <label style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                Tour package (destination)
                <select
                  value={draft.ticketPackageEventId || ""}
                  onChange={(event) => setDraft((prev) => ({ ...prev, ticketPackageEventId: event.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                >
                  <option value="">Choose package</option>
                  {packageEvents.map((event) => (
                    <option key={event.id} value={event.id}>{event.title}</option>
                  ))}
                </select>
                {!packageEvents.length ? (
                  <div style={{ marginTop: 4, color: "#b45309", fontSize: 11 }}>
                    Create a tour package in Ticket Operations (type: Tour package catalog).
                  </div>
                ) : null}
              </label>
            ) : usesSessionOnlyUi ? (
              <p style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                Uses choices already saved in the chat session — not a fixed route or tier on this node.
              </p>
            ) : isTravelReserveAction ? (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                  Travel bundle: uses <code>selected_travel_date</code>, hotel/vehicle answers, and <code>member_count</code>.
                </p>
                <AnswerVariableField
                  label="Hotel answer variable"
                  value={draft.hotelVariable}
                  onChange={(next) => setDraft((prev) => ({ ...prev, hotelVariable: next }))}
                  customVariables={availableVariables}
                  placeholder="selected_hotel"
                />
                <AnswerVariableField
                  label="Vehicle answer variable"
                  value={draft.vehicleVariable}
                  onChange={(next) => setDraft((prev) => ({ ...prev, vehicleVariable: next }))}
                  customVariables={availableVariables}
                  placeholder="selected_vehicle"
                />
                <label style={{ fontSize: 12 }}>
                  Fixed quantity / members (optional)
                  <input
                    value={draft.qty || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, qty: event.target.value }))}
                    placeholder="Leave empty — use member_count question"
                    style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                  />
                </label>
                <AnswerVariableField
                  label="Passenger/customer details variable"
                  value={draft.passengerVariable}
                  onChange={(next) => setDraft((prev) => ({ ...prev, passengerVariable: next }))}
                  customVariables={availableVariables}
                  placeholder="Optional"
                  hint="Optional. Use any variable from your traveler-details question."
                />
              </div>
            ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
              <label style={{ fontSize: 12 }}>
                Route / event / departure
                <select
                  value={draft.ticketEventId || ""}
                  onChange={(event) => setDraft((prev) => ({ ...prev, ticketEventId: event.target.value, ticketTierId: "" }))}
                  style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                >
                  <option value="">{isTierAvailabilityAction ? "Optional — travel uses selected date" : "Choose route/event"}</option>
                  {bookableEvents.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title}{eventKind(event) === "departure" ? " (departure)" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12 }}>
                Ticket tier
                <select
                  value={draft.ticketTierId || ""}
                  onChange={(event) => setDraft((prev) => ({ ...prev, ticketTierId: event.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                >
                  <option value="">{isTierAvailabilityAction ? "Optional — travel uses tour base tier" : "Choose tier"}</option>
                  {(ticketTiersByEvent[String(draft.ticketEventId)] || []).map((tier) => (
                    <option key={tier.id} value={tier.id}>{tier.name} ({tier.code})</option>
                  ))}
                </select>
              </label>
              {draft.actionId === "ticket.hold.create" ? (
                <label style={{ fontSize: 12, gridColumn: "1 / -1" }}>
                  <input
                    type="checkbox"
                    checked={draft.travelBooking !== false}
                    onChange={(event) => setDraft((prev) => ({ ...prev, travelBooking: event.target.checked }))}
                    style={{ marginRight: 8 }}
                  />
                  Travel agency bundle (tour + optional hotel + vehicle × travellers)
                </label>
              ) : null}
              <label style={{ fontSize: 12 }}>
                Fixed quantity / members
                <input
                  value={draft.qty || ""}
                  onChange={(event) => setDraft((prev) => ({ ...prev, qty: event.target.value }))}
                  placeholder="Leave empty — use member_count question"
                  style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                />
                <div style={{ marginTop: 4, color: "#64748b", fontSize: 11 }}>
                  Leave empty to use <code>member_count</code> from the previous Question node.
                </div>
              </label>
              <AnswerVariableField
                label="Seat answer variable"
                value={draft.seatVariable}
                onChange={(next) => setDraft((prev) => ({ ...prev, seatVariable: next }))}
                customVariables={availableVariables}
                placeholder="Use the Question node variable for seats"
                hint="Use the variable from your seat-name question (e.g. seats_name), not the quantity variable (e.g. seats_qty)."
              />
              <AnswerVariableField
                label="Passenger/customer details variable"
                value={draft.passengerVariable}
                onChange={(next) => setDraft((prev) => ({ ...prev, passengerVariable: next }))}
                customVariables={availableVariables}
                placeholder="Use only if passenger details are collected earlier"
                hint="Optional. Use any variable created by your customer-detail question or form."
              />
            </div>
            )}
            {!isPackageDeparturesAction && !usesSessionOnlyUi && !isTravelReserveAction && seatLabels.length > 0 ? (
              <div style={{ marginTop: 10, fontSize: 12, color: "#475569" }}>
                Available configured seats: {seatLabels.slice(0, 30).join(", ")}
                {seatLabels.length > 30 ? "..." : ""}
              </div>
            ) : null}
            {!isPackageDeparturesAction && !usesSessionOnlyUi && !isTravelReserveAction && !seatLabels.length && draft.ticketTierId ? (
              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                No assigned seat labels configured for this tier. Configure seats in Ticket Operations → Inventory Tiers.
              </div>
            ) : null}
          </div>
        )}

        {isCartAction && (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <b>Cart configuration</b>
            <div
              style={{
                marginTop: 8,
                padding: 10,
                borderRadius: 6,
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              {isSyncCartStateAction ? (
                <>
                  Place this <strong>before</strong> a Router set to <em>Order / cart</em>. It updates
                  cart totals, <strong>out-of-stock check</strong> (<code>cart.has_invalid_items</code>),
                  and <code>cart.stock_check_failed</code> if the store is busy (route to retry, not address).
                  Also sets whether the customer has <strong>old items from a previous visit</strong>.
                  Message nodes can use <code>{'{{productitems}}'}</code>, <code>{'{{invaliditems}}'}</code>, and{" "}
                  <code>{'{{oldcartitems}}'}</code>.
                </>
              ) : draft.actionId === "cart.validate_stock" ? (
                <>
                  Checks current cart against store inventory. Sets <code>cart.has_invalid_items</code> and{" "}
                  <code>{'{{invaliditems}}'}</code> for your message body. Does not remove lines — use{" "}
                  <em>Remove out-of-stock</em> after the customer taps your button.
                </>
              ) : draft.actionId === "cart.remove_oos" ? (
                <>
                  Removes unavailable lines from the current cart, then refreshes cart variables.
                  Connect your interactive node&apos;s button handle to this action in the flow canvas
                  (any button label and reply id).
                </>
              ) : draft.actionId === "cart.delete_orphans" ? (
                <>
                  Removes products left from an earlier visit or abandoned cart. Use after the customer
                  taps &quot;Remove old items&quot; (or similar) on your message.
                </>
              ) : draft.actionId === "cart.relink_orphans" ? (
                <>
                  Adds those old products back into the active cart when the customer chooses to keep them.
                </>
              ) : (
                <>Removes every product from the cart and starts fresh for this customer.</>
              )}
            </div>
          </div>
        )}

        {isAppointmentAction && (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <b>Appointment configuration</b>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
              <label style={{ fontSize: 12 }}>
                Appointment service
                <select
                  value={draft.appointmentTypeId || ""}
                  onChange={(event) => setDraft((prev) => ({ ...prev, appointmentTypeId: event.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                >
                  <option value="">Choose service</option>
                  {appointmentTypes.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 12 }}>
                Resource
                <select
                  value={draft.appointmentResourceId || ""}
                  onChange={(event) => setDraft((prev) => ({ ...prev, appointmentResourceId: event.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                >
                  <option value="">Any mapped resource</option>
                  {appointmentResources.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </select>
              </label>
              {draft.actionId === "appointment.hold.confirm" || draft.actionId === "appointment.payment.attach" ? (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    marginTop: 4,
                    padding: 10,
                    borderRadius: 6,
                    background: "#f0fdfa",
                    border: "1px solid #99f6e4",
                    color: "#134e4a",
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  {draft.actionId === "appointment.hold.confirm"
                    ? "No slot variable needed. Place this node immediately after Reserve slot, then your success message."
                    : "No slot variable needed. Place this node after Reserve slot, then connect to your Payment node."}
                </div>
              ) : (
                <AnswerVariableField
                  label="Slot/time answer variable"
                  value={draft.appointmentStartVariable}
                  onChange={(next) => setDraft((prev) => ({ ...prev, appointmentStartVariable: next }))}
                  customVariables={availableVariables}
                  placeholder="Use the Question node variable for selected slot"
                  hint={
                    draft.actionId === "appointment.availability"
                      ? "Use after an optional date question. Next message can show {{appointment_slots_text}}."
                      : "Question variable for slot pick (e.g. selected_slot). Customer may reply 1, 2, 3 from the list."
                  }
                />
              )}
            </div>
          </div>
        )}

        <FlowVariableInsertPicker
          customVariables={availableVariables}
          sectionIds={placeholderSectionIds}
          onInsert={copyPlaceholder}
          insertButtonLabel="Copy placeholder"
          label="Placeholders for Message nodes after this action"
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button type="button" onClick={() => setShowEditPopup(false)} style={{ padding: "8px 12px" }}>Cancel</button>
          <button type="button" onClick={save} style={{ padding: "8px 12px", background: "#0f766e", color: "#fff", border: "none", borderRadius: 6 }}>
            Save action
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div style={{ background: selected ? "#ecfdf5" : "#f0fdfa", border: `1px solid ${selected ? "#0f766e" : "#99f6e4"}`, borderRadius: 8, padding: 10, width: 220, minHeight: 92, position: "relative", boxShadow: selected ? "0 4px 8px rgba(15,118,110,.25)" : "0 2px 4px rgba(0,0,0,.1)" }}>
        <div style={{ position: "absolute", top: 8, right: 8, cursor: "pointer", color: "#0f766e", fontWeight: "bold" }} onClick={() => setShowMenu(!showMenu)}>...</div>
        {showMenu && (
          <div ref={menuRef} style={{ position: "absolute", top: 24, right: 8, background: "#fff", border: "1px solid #ddd", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,.15)", zIndex: 2 }}>
            <div style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid #eee" }} onClick={openEdit}>Edit</div>
            <div style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12 }} onClick={() => data.onDelete?.(id)}>Delete</div>
          </div>
        )}
        <Handle type="target" position={Position.Left} style={{ background: "#0f766e" }} />
        <Handle type="source" position={Position.Right} id={`${id}-main`} style={{ background: "#0f766e" }} />
        <div style={{ fontWeight: 700, color: "#0f766e", fontSize: 12, marginBottom: 6 }}>Vertical Action</div>
        <div style={{ fontSize: 12, color: "#334155", marginBottom: 4 }}>{safeData.title || "Configure action"}</div>
        <div style={{ fontSize: 10, color: "#64748b" }}>{ACTIONS.find((action) => action.value === safeData.actionId)?.label || safeData.actionId}</div>
      </div>
      {showEditPopup ? createPortal(popup, document.body) : null}
    </>
  );
};
